import { Root, Track, Range, Thumb } from '@radix-ui/react-slider'
import React, { useState } from 'react'
import styles from './index.module.css'
import { IconHandle } from '@s4l/icons/IconHandle'
import { getDirection } from '@s4l/utils/library/rtl'
import useInputValue from '../hooks/useInputValue'
import { cn, type UtilityClassNames } from '@s4l/utility-css'

interface SliderProps {
  name: string | string[]
  min: number
  max: number
  defaultValue?: number | number[]
  disabled?: boolean
  onChange?: (value: number | number[]) => void
  ariaLabel?: string
  className?: UtilityClassNames
  step?: number
  value?: number[]
}

const Slider = ({
  name,
  min = 0,
  max = 100,
  defaultValue,
  disabled,
  onChange,
  ariaLabel,
  className,
  step,
  value,
}: SliderProps) => {
  const names = Array.isArray(name) ? name : [name]

  const defaultValueArray = Array.isArray(defaultValue) ? defaultValue : [defaultValue]

  const [minValue, setMinValue] = useState(defaultValueArray[0] ?? min)
  const [maxValue, setMaxValue] = useState(defaultValueArray[1] ?? max)

  const { onInputChange: onInputChangeMax } = useInputValue({
    name: names[1],
    defaultValue: maxValue,
    falseValue: max,
    disabled: !names[1],
    setInputValue: newValue => {
      if (typeof newValue === 'number') {
        setMaxValue(newValue)
      }
    },
  })

  const { onInputChange: onInputChangeMin } = useInputValue({
    name: names[0],
    defaultValue: minValue,
    falseValue: min,
    setInputValue: newValue => {
      if (typeof newValue === 'number') {
        setMinValue(newValue)
      }
    },
  })

  const handleChange = (newValue: number | number[]) => {
    const isMultiple = Array.isArray(newValue)
    const newValueArray = isMultiple ? newValue : [newValue]

    onInputChangeMin(newValueArray[0])
    setMinValue(newValueArray[0])

    onInputChangeMax(isMultiple ? newValueArray[1] : newValueArray[0])
    setMaxValue(isMultiple ? newValueArray[1] : newValueArray[0])

    onChange?.(newValue)
  }

  return (
    <Root
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      min={min}
      max={max}
      className={cn(styles.root, className)}
      data-testid="slider-root"
      value={value || (names.length === 2 ? [minValue, maxValue] : [minValue])}
      onValueChange={handleChange}
      minStepsBetweenThumbs={1}
      disabled={disabled}
      dir={getDirection()}
      step={step}
    >
      <Track className={styles.track}>
        <Range className={styles.range} />
      </Track>
      <Thumb className={styles.thumb} data-testid="thumb-min">
        <IconHandle />
      </Thumb>
      {names.length === 2 && (
        <Thumb className={styles.thumb} data-testid="thumb-max">
          <IconHandle />
        </Thumb>
      )}
    </Root>
  )
}

Slider.displayName = 'Form.Slider'
export default Slider
